/**
 * SendContext - wraps the React Query mutation that posts a
 * question (or fallback decision) and exposes its state to
 * everything that needs to react: the Composer's loading state,
 * the MessageStream's "thinking" indicator, and the disabled
 * state of the FallbackDecisionButtons.
 *
 * Optimistic flow for ask:
 *   1. onMutate    - append the user's turn to the conversation
 *                    cache so the bubble appears instantly. Snapshot
 *                    the prior cache for rollback.
 *   2. (in flight) - ThinkingIndicator visible at the bottom of
 *                    the stream. Composer's send button locked.
 *   3. onSuccess   - invalidate the conversation key so the next
 *                    render picks up the authoritative server
 *                    state (which includes the assistant's reply
 *                    + sources + awaiting_fallback_decision).
 *   4. onError     - restore the snapshot, surface the error to
 *                    the Composer for inline display.
 *
 * Fallback decision is the same shape but no user-message append -
 * the server already has the prompt's context in session and just
 * sends back the follow-up answer.
 */
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  askQuestion,
  previewSources,
  sendFallbackDecision,
  type AskRequest,
  type ConversationResponse,
  type ConversationTurn,
  type MessageSource,
  type ResponseLength,
} from '../api/conversation';
import { ApiError } from '../api/client';
import { useAudioReply } from './AudioReplyContext';
import { useAskFilters } from '../dashboard/AskFiltersContext';

const CONVERSATION_KEY = ['conversation'] as const;

interface AskOptions {
  attachmentIds?: string[];
  audioRequested?: boolean;
}

interface SendContextValue {
  ask: (message: string, length: ResponseLength, options?: AskOptions) => void;
  decide: (decision: 'accept' | 'decline') => void;
  isPending: boolean;
  errorMessage: string | null;
  clearError: () => void;
  /** Optimistic source list from /api/chat/preview, populated as
   *  soon as Vertex search settles (~500ms after send). null while
   *  no preview is in flight; empty array while preview is in
   *  flight; populated array once preview returns. The
   *  ThinkingIndicator renders a "Found in foo, bar" subtitle when
   *  this is non-null. Cleared when the real /api/chat completes
   *  (or errors) - by that point the actual sources land on the
   *  refetched conversation turn. */
  previewedSources: MessageSource[] | null;
}

const SendContext = createContext<SendContextValue | null>(null);

export function SendProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Optimistic source list. Set when /api/chat/preview returns
  // (~500ms after send). Cleared when the real ask settles. The
  // ThinkingIndicator reads this to render "Found in foo, bar"
  // while the LLM is still thinking, so the user sees signal
  // long before the answer comes back.
  const [previewedSources, setPreviewedSources] = useState<MessageSource[] | null>(null);
  // Rail filters merge into every ask. Read here so the Composer
  // doesn't have to know about filter state - it just calls ask().
  const { documentIds, language } = useAskFilters();
  // Audio playback handle. Only `stop` is called directly here:
  // we kill any prior playback on send (don't bleed previous
  // audio into a fresh question). Actual playback start happens
  // on conversation refetch via AudioPlayer's cache subscriber,
  // which gives the playback a timestamp binding so the
  // per-message stop button can render on the right bubble.
  const { stop: stopAudio } = useAudioReply();

  // ── Ask ──
  const askMutation = useMutation({
    mutationFn: (req: AskRequest) => askQuestion(req),
    // Optimistic append. We hold the snapshot in `context` so
    // onError can roll back.
    onMutate: async (req) => {
      setErrorMessage(null);
      await queryClient.cancelQueries({ queryKey: CONVERSATION_KEY });

      const snapshot = queryClient.getQueryData<ConversationResponse>(
        CONVERSATION_KEY,
      );
      const optimisticTurn: ConversationTurn = {
        role: 'user',
        content: req.message,
        timestamp: new Date().toISOString(),
        // Include staged attachment ids so the bubble shows the
        // thumbnail immediately rather than only after refetch.
        attachment_ids: req.attachment_ids,
      };
      queryClient.setQueryData<ConversationResponse>(
        CONVERSATION_KEY,
        (prev) => ({
          turns: [...(prev?.turns ?? []), optimisticTurn],
          thread_id: prev?.thread_id ?? '',
        }),
      );
      return { snapshot };
    },
    onSuccess: () => {
      // Audio playback intentionally NOT fired from here. The
      // server now stamps audio_url onto the assistant turn
      // (chat_service.py persists it on the appended turn) and
      // AudioPlayer subscribes to the conversation cache -
      // when the refetch lands the subscriber picks up the
      // turn + audio_url + timestamp and starts playback bound
      // to that specific bubble's timestamp. Critical for the
      // per-message stop button: without the timestamp binding
      // we'd have no way to render the button on the right
      // turn. Adds ~one refetch of latency to the start of
      // playback, acceptable trade.
    },
    onError: (err, _req, ctx) => {
      // Restore the prior cache so the optimistic user bubble
      // disappears - they didn't actually send.
      if (ctx?.snapshot) {
        queryClient.setQueryData(CONVERSATION_KEY, ctx.snapshot);
      }
      setErrorMessage(
        err instanceof ApiError
          ? err.message
          : 'Could not send your message. Please try again.',
      );
    },
    onSettled: () => {
      // Always refetch - both success (to pick up the assistant's
      // reply) and error (to make sure cache matches server state).
      queryClient.invalidateQueries({ queryKey: CONVERSATION_KEY });
      // Also nudge the Previous Conversations sidebar list so a
      // brand-new thread appears (or an existing thread bumps to
      // the top) without waiting for the focus refetch.
      queryClient.invalidateQueries({ queryKey: ['user-threads'] });
      // The optimistic preview line drops away as soon as the real
      // answer arrives - by that point the actual sources are on
      // the refetched assistant turn (MessageSources renders
      // them).
      setPreviewedSources(null);
    },
  });

  // ── Fallback decision ──
  const decisionMutation = useMutation({
    mutationFn: (decision: 'accept' | 'decline') =>
      sendFallbackDecision(decision),
    onMutate: async () => {
      setErrorMessage(null);
      await queryClient.cancelQueries({ queryKey: CONVERSATION_KEY });
      const snapshot = queryClient.getQueryData<ConversationResponse>(
        CONVERSATION_KEY,
      );
      // Optimistically clear the awaiting flag on the most-recent
      // assistant turn so the Yes/No buttons disappear immediately.
      // The server-side update on next refetch is authoritative.
      queryClient.setQueryData<ConversationResponse>(
        CONVERSATION_KEY,
        (prev) => {
          if (!prev) return prev;
          const turns = [...prev.turns];
          for (let i = turns.length - 1; i >= 0; i--) {
            const t = turns[i];
            if (t && t.role === 'assistant' && t.awaiting_fallback_decision) {
              turns[i] = { ...t, awaiting_fallback_decision: false };
              break;
            }
          }
          return { ...prev, turns };
        },
      );
      return { snapshot };
    },
    onError: (err, _decision, ctx) => {
      if (ctx?.snapshot) {
        queryClient.setQueryData(CONVERSATION_KEY, ctx.snapshot);
      }
      setErrorMessage(
        err instanceof ApiError
          ? err.message
          : 'Could not record your choice. Please try again.',
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CONVERSATION_KEY });
      queryClient.invalidateQueries({ queryKey: ['user-threads'] });
    },
  });

  const ask = useCallback(
    (message: string, length: ResponseLength, options?: AskOptions) => {
      // Kill any audio still playing from a previous turn - it
      // sounds messy if a new question lands while the last
      // answer is mid-sentence, and the user would have no obvious
      // way to silence it once the next reply arrives on top.
      stopAudio();

      const docIdsArr = documentIds.size > 0 ? Array.from(documentIds) : undefined;
      const lang = language || undefined;

      // Fire the optimistic source preview in parallel. Cheap
      // Vertex-only call (~500ms); whichever races back first wins
      // - usually preview, sometimes the real ask if Vertex is
      // unusually slow. Empty array starting state lets the
      // ThinkingIndicator show "Searching…" copy until preview
      // settles, instead of switching from "no signal" to "found"
      // mid-stream.
      setPreviewedSources([]);
      previewSources({
        message,
        document_ids: docIdsArr,
        language: lang,
      })
        .then((res) => {
          // Only apply if the ask is still in flight - otherwise the
          // real answer has already landed and we're just adding
          // noise.
          if (askMutation.isPending) {
            setPreviewedSources(res.sources || []);
          }
        })
        .catch(() => {
          // Preview is best-effort. On failure clear the slot so the
          // ThinkingIndicator drops back to plain pulsing dots.
          if (askMutation.isPending) {
            setPreviewedSources(null);
          }
        });

      askMutation.mutate({
        message,
        response_length: length,
        attachment_ids: options?.attachmentIds?.length
          ? options.attachmentIds
          : undefined,
        audio_requested: options?.audioRequested || undefined,
        // Rail filters - only sent when the user has narrowed
        // away from defaults. Empty document Set means "all";
        // empty language means "auto-detect". Server treats
        // both omitted-and-empty as "no constraint".
        document_ids: docIdsArr,
        language: lang,
      });
    },
    [askMutation, documentIds, language, stopAudio],
  );
  const decide = useCallback(
    (decision: 'accept' | 'decline') => {
      decisionMutation.mutate(decision);
    },
    [decisionMutation],
  );
  const clearError = useCallback(() => setErrorMessage(null), []);

  const value: SendContextValue = {
    ask,
    decide,
    isPending: askMutation.isPending || decisionMutation.isPending,
    errorMessage,
    clearError,
    previewedSources,
  };

  return <SendContext.Provider value={value}>{children}</SendContext.Provider>;
}

export function useSend(): SendContextValue {
  const ctx = useContext(SendContext);
  if (!ctx) {
    throw new Error('useSend must be used inside <SendProvider>');
  }
  return ctx;
}
